import React from 'react';
import { Container, Title, Text, SimpleGrid, ThemeIcon, Button, Group, Badge, Timeline, Card, createStyles, rem, Stack } from '@mantine/core';
import { IconRocket, IconBrain, IconTools, IconHierarchy, IconBulb, IconUserCheck, IconArrowRight } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

const useStyles = createStyles((theme) => ({
  heroTitle: {
    fontSize: rem(60),
    fontWeight: 900,
    lineHeight: 1.1,
    backgroundImage: `linear-gradient(135deg, ${theme.colors.cyan[4]} 0%, ${theme.colors.grape[5]} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: theme.spacing.md,
    textAlign: 'center',

    [theme.fn.smallerThan('sm')]: {
      fontSize: rem(40),
    },
  },

  heroSubtitle: {
    fontSize: rem(24),
    textAlign: 'center',
    color: theme.colors.gray[4],
    maxWidth: 800,
    margin: '0 auto',
    marginBottom: theme.spacing.xl,
    lineHeight: 1.5,
    [theme.fn.smallerThan('sm')]: {
      fontSize: rem(18),
    },
  },

  glassCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: theme.radius.lg,
    padding: theme.spacing.xl,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',

    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: `1px solid ${theme.colors.cyan[8]}`,
    },
  },

  sectionTitle: {
    fontSize: rem(36),
    fontWeight: 700,
    marginBottom: theme.spacing.md,
    color: theme.white,
    [theme.fn.smallerThan('sm')]: {
      fontSize: rem(28),
    },
  },

  highlightText: {
    color: theme.colors.cyan[4],
    fontWeight: 600,
  }
}));

const Feature = ({ icon: Icon, title, description }) => {
  const { classes, theme } = useStyles();
  return (
    <Card className={classes.glassCard}>
      <ThemeIcon size={50} radius="md" variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }}>
        <Icon size={28} stroke={1.5} />
      </ThemeIcon>
      <Text mt="md" mb={5} size="lg" weight={700} color="white">
        {title}
      </Text>
      <Text size="sm" color="dimmed" lh={1.6}>
        {description}
      </Text>
    </Card>
  );
};

const About = () => {
  const { classes } = useStyles();

  return (
    <div style={{ paddingTop: 80, paddingBottom: 60, minHeight: '100vh', backgroundColor: '#050505', backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)' }}>
      <Container size="lg">

        {/* Hero Section */}
        <div style={{ padding: '80px 0', textAlign: 'center' }}>
          <Group position="center" mb="md">
            <Badge size="lg" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>made in india</Badge>
            <Badge size="lg" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>vocal for local</Badge>
          </Group>
          <Title className={classes.heroTitle}>
            Crafting the Future,<br /> One Micron at a Time
          </Title>
          <Text className={classes.heroSubtitle}>
            Our mission is to fuel India’s innovation engine by turning radical ideas into tangible reality. We exist not just to print, but to perfect.
          </Text>
        </div>

        <SimpleGrid cols={2} spacing={50} breakpoints={[{ maxWidth: 'md', cols: 1 }]}>

          {/* The Why */}
          <div className={classes.glassCard}>
            <Group mb="md">
              <ThemeIcon size="xl" radius="xl" color="cyan" variant="light"><IconBulb /></ThemeIcon>
              <Title order={2} color="white">The Why</Title>
            </Group>
            <Title order={4} color="cyan" mb="sm">We Believe in the Power of the Precise Idea</Title>
            <Text color="gray.4" size="lg" mb="sm">
              Every revolution begins with a spark: an idea so clear in the mind’s eye, yet so often lost in translation. We are engineering graduates who have felt that frustration.
            </Text>
            <Text color="gray.4" size="lg">
              The gap between a perfect concept and a physical prototype shouldn’t be a chasm of compromise. It should be a seamless bridge, built with absolute fidelity.
            </Text>
          </div>

          {/* The Enemy */}
          <div className={classes.glassCard}>
            <Group mb="md">
              <ThemeIcon size="xl" radius="xl" color="red" variant="light"><IconBrain /></ThemeIcon>
              <Title order={2} color="white">The Problem</Title>
            </Group>
            <Title order={4} color="red" mb="sm">The Compromise</Title>
            <Text color="gray.4" size="lg" mb="sm">
              The old way forces a choice: either you get convenience with generic results, or you get customization through a labyrinth of intermediaries and uncertainty.
            </Text>
            <Text color="gray.4" size="lg">
              This compromise kills ingenuity. We reject the notion that you must sacrifice precision for speed, or expert oversight for control.
            </Text>
          </div>

        </SimpleGrid>

        {/* The How Section */}
        <div style={{ marginTop: 100, marginBottom: 100 }}>
          <Title className={classes.sectionTitle} align="center">The How: Engineering-Grade Creation, On Your Terms</Title>
          <Text align="center" color="dimmed" mb={20} size="lg">
            We built a different system. Every print is handled in-house, from initial design to final layer, by a team of engineering experts who speak your language.
          </Text>
          {/* <Text align="center" color="cyan" weight={500} mb={50} size="xl">
            This isn’t just manufacturing; it’s a collaboration.
          </Text> */}

          <SimpleGrid cols={3} spacing="xl" breakpoints={[{ maxWidth: 'md', cols: 1 }]}>
            <Feature
              icon={IconUserCheck}
              title="Expert-Led, Not Outsourced"
              description="Your vision is shepherded by engineers, not order-takers. We provide the expertise so you retain complete control."
            />
            <Feature
              icon={IconTools}
              title="Precision as a Principle"
              description="We obsess over the details you care about: dimensional accuracy, material integrity, surface finish: down to the last micron."
            />
            <Feature
              icon={IconRocket}
              title="Convenience Meets Craft"
              description="We combine the seamless digital experience you expect with the meticulous hand of a craftsman. Create, edit, and explore in a feedback loop of pure execution."
            />
          </SimpleGrid>
        </div>

        {/* The Invitation / CTA */}
        <Card radius="lg" p={60} sx={(theme) => ({
          background: `linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(20,20,20,0.8) 100%)`,
          border: `1px solid ${theme.colors.cyan[9]}`,
          textAlign: 'center',
          backdropFilter: 'blur(10px)',
          [theme.fn.smallerThan('sm')]: {
            padding: theme.spacing.xl,
          }
        })}>
          <Stack align="center" spacing="xl">
            <Title order={2} color="white" size={40} sx={(theme) => ({ [theme.fn.smallerThan('sm')]: { fontSize: 28 } })}>Join the Revolution</Title>
            <Text color="gray.3" size="xl" maw={700} sx={(theme) => ({ [theme.fn.smallerThan('sm')]: { fontSize: 16 } })}>
              This is more than 3D printing. It’s a movement to make India a global hub for designed-in-India, made-in-India innovation.
              We are here for the creators, the engineers, and the visionaries who see what could be and refuse to settle for what is.
            </Text>
            <Text size={24} weight={700} variant="gradient" gradient={{ from: 'cyan', to: 'lime' }}>
              If you can imagine it, we can build it.
            </Text>

            <Group mt="xl">
              <Button component={Link} to="/upload-and-print" size="xl" radius="xl" variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }} rightIcon={<IconArrowRight />}>
                Start Creating
              </Button>
              <Button component={Link} to="/store" size="xl" radius="xl" variant="outline" color="cyan">
                Explore Store
              </Button>
            </Group>
          </Stack>
        </Card>

      </Container>
    </div>
  );
}

export default About;